import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { chatStorage } from '@/lib/chatStorage';
import { audioManager } from '@/lib/audioManager';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Security: Clear OAuth tokens from URL fragment immediately
    const clearSensitiveUrlParams = () => {
      if (window.location.hash && window.location.hash.includes('access_token')) {
        // Replace current URL without the hash fragment
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Security: Only log non-sensitive info in production
        if (import.meta.env.DEV) {
          console.log('[AuthContext] Auth event:', event);
        }
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Clear URL tokens after successful auth
        if (event === 'SIGNED_IN') {
          clearSensitiveUrlParams();
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error && import.meta.env.DEV) {
        console.error('[AuthContext] Session error');
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Also clear on initial load if tokens present
      clearSensitiveUrlParams();
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Stop any playing audio and clear chat history on sign out
    audioManager.stop();
    chatStorage.clearAll();

    // Best-effort reset for test accounts — the edge function checks
    // server-side whether the caller is a test account (no emails in bundle)
    const token = session?.access_token;
    if (token) {
      try {
        await supabase.functions.invoke('reset-test-user', {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // swallow — best-effort reset
      }
    }

    // Clear local state immediately for instant UI feedback
    setUser(null);
    setSession(null);

    // Sign out from Supabase with error handling
    try {
      await supabase.auth.signOut();
    } catch {
      console.warn('[AuthContext] Sign out may not have completed on server');
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
